import { defineStore } from 'pinia'
import { createTooltip } from 'floating-vue';
import { watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';

declare const useRuntimeConfig: typeof import('nuxt/app')['useRuntimeConfig'];
declare const useAsyncData: typeof import('nuxt/app')['useAsyncData'];
declare const $fetch: typeof import('ofetch').$fetch;
declare const useCookie: typeof import('nuxt/app')['useCookie'];

type ResolvedRuntimeConfig = { public: Record<string, any> } & Record<string, any>;

const getRuntimeConfig = () => useRuntimeConfig() as ResolvedRuntimeConfig;

const ATTENTION_MODAL_COOKIE_KEY = 'attention_acknowledged';
const ATTENTION_MODAL_COOKIE_VALUE = 'ack';
const ATTENTION_MODAL_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days
import {
    type Message,
    type MessageDataPage,
    type ReviewData,
    type SearchPostData,
    type SearchResponseEventType,
    type RelatedQuestionsPayload,
    Evaluate,
    type MessageDataScenarios,
    ChatMode,
    type FileData,
    type SearchOptions,
    type SearchPayload,
    type SearchResult,
    type FollowUpTask,
    type FollowUpStatus,
    type DeepResearchPayload,
    type DeepResearchStep,
    type DeepResearchReference,
    type FollowupQuestion,
    type IntentSuggestion,
    type SpecialMode,
    type IntentSuggestionsData,
} from '../utils/type';
import 'floating-vue/dist/style.css'
import { micromark } from 'micromark';
import { useI18n } from 'vue-i18n';
import { useDeepResearchStore } from './deepResearchStore';

const STREAM_TIMEOUT_MS = 90000;

export const useChatStore = defineStore('chatData', {
    state: () =>
    ({
        reviewModal: false,
        windowExtend: false,
        windowClosed: true,
        userId: '',
        sessionId: '',
        reviewComment: '',
        reviewBtn: '',
        messages: [],
        searchResponseMsg: '',
        searchMarkdownMsg: '',
        searchResponseClose: false,
        searchAnswerPages: [],
        searchAnswerTooltips: [],
        sendBadReviewHistoryId: '',
        language: 'ja',
        uiTextQuestionInputPlaceholder: '',
        uiTextAttention: '',
        uiTextModeSwitched: '',
        startMessage: '',
        searchAnswerError: "",
        isLanguageMenuDsp: false,
        attentionModal: true,
        languageMenus: [],
        scenarioMessage: "",
        isNewWindow: false,
        isAnswerScenario: false,
        startScenarioMessage: '',
        keywordStr: '',
        keywordUrl: '',
        questions: [],
        relatedQuestions: [],
        relatedQuestionsLang: '',
        isIframe: false,
        isDspStartChat: true,
        chatMode: ChatMode.general,
        changeChatModeGeneralMessage: '',
        changeChatModeGomiMessage: '',
        chatModeGomiStartMessage: '',
        isAutoScrollToBottom: true,
        promptVersion: '', // プロンプトプレビュー用
        followUpQueue: [],
        followUpHistory: [],
        followUpSummary: '',
        isProcessingFollowUps: false,
        activePanel: 'chat',
        activeSearchResult: null,
        modes: [],
        currentMode: '',
        deepResearch: {
            isActive: false,
            isPanelOpen: false,
            keywords: [] as string[],
            progressLog: [] as string[],
            steps: [] as DeepResearchStep[],
            references: [] as DeepResearchReference[],
            summary: '',
            metadata: {} as Record<string, unknown>,
            followUpQuestions: [] as string[],
            wasManuallyClosed: false,
            hasUnseenUpdates: false,
        },
        lastSearchPayload: null,
        streamBuffer: '',
        hasReceivedCloseEvent: false,
        streamTimedOut: false,
        streamTimeoutHandle: null,
        activeStreamController: null,
        isStreaming: false,
        canRetryLastSearch: false,
        suppressModeChangeNotice: false,
        sessionQuestionLengthTotal: 0,
    } as {
        reviewModal: boolean;
        windowExtend: boolean;
        windowClosed: boolean;
        userId: string;
        sessionId: string | null;
        reviewComment: string;
        reviewBtn: string;
        messages: Message[];
        searchResponseMsg: string;
        searchMarkdownMsg: string;
        searchResponseClose: boolean;
        searchAnswerPages: MessageDataPage[];
        searchAnswerTooltips: any[];
        sendBadReviewHistoryId: string;
        language: string;
        uiTextQuestionInputPlaceholder: string;
        uiTextAttention: string;
        uiTextModeSwitched: string;
        startMessage: string;
        searchAnswerError: string;
        isLanguageMenuDsp: boolean;
        attentionModal: boolean;
        languageMenus: string[];
        scenarioMessage: string;
        isNewWindow: boolean;
        isAnswerScenario: boolean;
        startScenarioMessage: string;
        keywordStr: string;
        keywordUrl: string;
        questions: string[];
        relatedQuestions: string[];
        relatedQuestionsLang: string;
        isIframe: boolean;
        isDspStartChat: boolean;
        chatMode: ChatMode;
        changeChatModeGeneralMessage: string;
        changeChatModeGomiMessage: string;
        chatModeGomiStartMessage: string;
        isAutoScrollToBottom: boolean;
        promptVersion: string;
        followUpQueue: FollowUpTask[];
        followUpHistory: FollowUpTask[];
        followUpSummary: string;
        isProcessingFollowUps: boolean;
        activePanel: 'chat' | 'history';
        activeSearchResult: SearchResult | null;
        modes: SpecialMode[];
        currentMode: string;
        deepResearch: {
            isActive: boolean;
            isPanelOpen: boolean;
            keywords: string[];
            progressLog: string[];
            steps: DeepResearchStep[];
            references: DeepResearchReference[];
            summary: string;
            metadata: Record<string, unknown>;
            followUpQuestions: string[];
            wasManuallyClosed: boolean;
            hasUnseenUpdates: boolean;
        };
        lastSearchPayload: SearchPayload | null;
        streamBuffer: string;
        hasReceivedCloseEvent: boolean;
        streamTimedOut: boolean;
        streamTimeoutHandle: ReturnType<typeof setTimeout> | null;
        activeStreamController: AbortController | null;
        isStreaming: boolean;
        canRetryLastSearch: boolean;
        suppressModeChangeNotice: boolean;
        sessionQuestionLengthTotal: number;
    }),
    getters: {
        followUpTotalCount: (state) => state.followUpQueue.length,
        followUpCompletedCount: (state) => state.followUpQueue.filter((task) => task.status === 'success' || task.status === 'error').length,
        followUpRunningCount: (state) => state.followUpQueue.filter((task) => task.status === 'running').length,
    },
    actions: {
        resolveI18nText(i18nMap: Record<string, string> | undefined, fallbackText: string = ''): string {
            if (!i18nMap) {
                return fallbackText;
            }

            const candidates: string[] = [];
            if (this.language) {
                candidates.push(this.language);
                candidates.push(this.language.replace(/-/g, ''));
                candidates.push(this.language.split('-')[0] ?? this.language);
            }

            for (const lang of candidates) {
                const value = i18nMap[lang];
                if (typeof value === 'string' && value.trim().length > 0) {
                    return value;
                }
            }

            return fallbackText;
        },
        getModeDisplayName(mode: SpecialMode): string {
            return this.resolveI18nText(mode.name_i18n, mode.name);
        },
        getModeDisplayDescription(mode: SpecialMode): string {
            return this.resolveI18nText(mode.description_i18n, mode.description ?? '');
        },
        async ensureUserId(): Promise<string> {
            if (this.userId) {
                return this.userId;
            }

            const config = getRuntimeConfig();
            const endpoint = config.public.userIdGenerateApi;
            if (!endpoint) {
                console.warn('USER_ID_GENERATE_API is not configured');
                return '';
            }

            try {
                const res = await $fetch<string | { user_id?: string; userId?: string; id?: string }>(endpoint, {
                    method: 'GET',
                });
                const generated =
                    typeof res === 'string'
                        ? res
                        : res?.user_id || res?.userId || res?.id;

                if (generated) {
                    this.userId = generated;
                }
            } catch (error) {
                console.error('ユーザID取得エラー', error);
            }

            return this.userId;
        },
        acknowledgeAttentionModal() {
            this.attentionModal = false;
        },
        async initChat(options: { preserveMessages?: boolean } = {}) {
            const { preserveMessages = false } = options;
            if (!preserveMessages) {
                this.messages = [];
            }
            this.resetFollowUpData();

            this.chatMode = ChatMode.general;
            console.log("default chatmode", this.chatMode);

            await this.ensureUserId();
            await this.getSessionId();

            // モード一覧取得
            await this.fetchModes();
        },
        /**
         * モード一覧を取得
         */
        async fetchModes() {
            const config = getRuntimeConfig();
            try {
                const res = await $fetch<SpecialMode[]>(config.public.specialmodesApi, {
                    query: {
                        ui_lang: this.language,
                        supported_languages: (config.public.supportedLanguages || []).join(','),
                    },
                });

                // general, gomi は設定に含まれていなければ除外。DBのモードはそのまま表示
                const allowedModes = (config.public.useChatMode as string[]) || [];
                const defaultModeCodes = ["general", "gomi"];

                this.modes = res.filter((m) => {
                    if (defaultModeCodes.includes(m.code)) {
                        return allowedModes.includes(m.code);
                    }
                    return true;
                });

                // 現在のchatModeに合わせてcurrentModeを設定
                if (!this.currentMode && this.modes.length > 0) {
                    // 既存のchatModeがリストにあるか確認
                    const current = this.modes.find((m) => m.code === this.chatMode);
                    if (current) {
                        this.currentMode = current.code;
                    } else if (this.modes.length > 0) {
                        // なければリストの最初のもの（通常general）
                        this.currentMode = this.modes[0]!.code;
                    }
                }
            } catch (e) {
                console.error("モード一覧取得エラー", e);
            }
        },
        /**
         * モードを設定
         */
        setMode(modeCode: string) {
            if (this.currentMode === modeCode) return;

            this.currentMode = modeCode;
            this.chatMode = modeCode as ChatMode;
        },
        attachSessionContext(message: Message) {
            if (this.sessionId) {
                message.sessionId = this.sessionId;
            }

            return message;
        },
        async initDsp(options: { preserveMessages?: boolean } = {}) {
            const { preserveMessages = false } = options;
            if (this.attentionModal && !preserveMessages) {
                // 注意事項画面を表示している場合
                // メッセージをクリア
                this.messages = [];
            }

            if (this.chatMode == ChatMode.general) {
                // 総合案内モードの場合

                // 初期シナリオ取得
                let firstScenarios = await this.getFirstScenarios();

                // 初期メッセージ
                let message = {
                    author: "support",
                    type: "text",
                    text: this.startMessage,
                    pages: [],
                    historyId: "",
                    answer: false,
                    scenarios: [],
                    isGoodReviewed: false,
                    isBadReviewed: false,
                    copyText: this.searchMarkdownMsg,
                    language: this.language,
                    reviewPattern: 0,
                    keywordStr: this.keywordStr,
                    keywordUrl: this.keywordUrl,
                    questions: [],
                    imageUrl: ""
                } as Message;
                message = this.attachSessionContext(message);
                this.messages.push(message);

                if (firstScenarios.length > 0) {
                    //初期シナリオがある場合
                    message = {
                        author: 'support',
                        text: this.startScenarioMessage,
                        type: 'text',
                        pages: [],
                        id: Math.random(),
                        historyId: "",
                        answer: false,
                        scenarios: firstScenarios,
                        isGoodReviewed: false,
                        isBadReviewed: false,
                        copyText: "",
                        language: this.language,
                        reviewPattern: 0,
                        keywordStr: this.keywordStr,
                        keywordUrl: this.keywordUrl,
                        questions: [],
                        imageUrl: ""
                    } as Message;
                    message = this.attachSessionContext(message);
                    this.messages.push(message);
                }
            }
            else if (this.chatMode == ChatMode.gomi) {
                // ごみ分別案内モードの場合
                let message = {
                    author: "support",
                    type: "text",
                    text: this.chatModeGomiStartMessage,
                    pages: [],
                    historyId: "",
                    answer: false,
                    scenarios: [],
                    isGoodReviewed: false,
                    isBadReviewed: false,
                    copyText: "",
                    language: this.language,
                    reviewPattern: 0,
                    keywordStr: "",
                    keywordUrl: "",
                    questions: [],
                    imageUrl: ""
                } as Message;
                message = this.attachSessionContext(message);
                this.messages.push(message);
            }
        },
        async restartSession(options: { preserveWindowExtend?: boolean; preserveMessages?: boolean } = {}) {
            const { preserveWindowExtend = false, preserveMessages = true } = options;
            const previousMode = this.currentMode;
            const previousChatMode = this.chatMode;
            this.suppressModeChangeNotice = true;
            this.clearStreamTimeout();

            if (this.activeStreamController) {
                try {
                    this.activeStreamController.abort();
                } catch (error) {
                    console.debug('abort controller cleanup error', error);
                }
                this.activeStreamController = null;
            }

            this.isStreaming = false;
            this.streamTimedOut = false;
            this.hasReceivedCloseEvent = false;
            this.streamBuffer = '';
            this.canRetryLastSearch = false;
            this.sessionQuestionLengthTotal = 0;

            this.searchResponseMsg = '';
            this.searchMarkdownMsg = '';
            this.searchResponseClose = false;
            this.searchAnswerPages = [];
            this.searchAnswerTooltips.forEach((tooltip: any) => {
                try {
                    if (typeof tooltip?.destroy === 'function') {
                        tooltip.destroy();
                    } else if (typeof tooltip?.dispose === 'function') {
                        tooltip.dispose();
                    }
                } catch (error) {
                    console.debug('tooltip cleanup error', error);
                }
            });
            this.searchAnswerTooltips = [];
            this.keywordStr = '';
            this.keywordUrl = '';
            this.questions = [];
            this.isAnswerScenario = false;

            this.reviewModal = false;
            this.reviewComment = '';
            this.reviewBtn = '';
            this.sendBadReviewHistoryId = '';

            this.isLanguageMenuDsp = false;
            this.isDspStartChat = true;
            if (!preserveWindowExtend) {
                this.windowExtend = false;
            }

            this.resetDeepResearch(false);
            this.resetFollowUpData();
            if (!preserveMessages) {
                this.messages = [];
            }

            const hasExistingMessages = this.messages.length > 0;

            await this.initChat({ preserveMessages });
            const preservedMode = previousMode || previousChatMode;
            if (preservedMode) {
                this.chatMode = preservedMode as ChatMode;
                if (!this.currentMode) {
                    this.currentMode = preservedMode;
                }
            }
            if (preserveMessages && hasExistingMessages) {
                const dividerMessage: Message = {
                    author: 'support',
                    type: 'session_divider',
                    text: '',
                    pages: [],
                    historyId: '',
                    answer: false,
                    scenarios: [],
                    isGoodReviewed: false,
                    isBadReviewed: false,
                    copyText: '',
                    language: this.language,
                    reviewPattern: 0,
                    keywordStr: '',
                    keywordUrl: '',
                    questions: [],
                    imageUrl: '',
                };
                this.messages.push(this.attachSessionContext(dividerMessage));
            }
            await this.initDsp({ preserveMessages });
            this.suppressModeChangeNotice = false;
        },
        incrementSessionQuestionLength(length: number) {
            if (!Number.isFinite(length) || length <= 0) {
                return;
            }
            this.sessionQuestionLengthTotal += length;
        },
        resetDeepResearch(keepPanelOpen = false) {
            const shouldKeepOpen = keepPanelOpen && this.deepResearch.isPanelOpen;
            this.deepResearch.isActive = false;
            this.deepResearch.keywords = [];
            this.deepResearch.progressLog = [];
            this.deepResearch.steps = [];
            this.deepResearch.references = [];
            this.deepResearch.summary = '';
            this.deepResearch.metadata = {};
            this.deepResearch.followUpQuestions = [];
            this.deepResearch.hasUnseenUpdates = false;
            this.deepResearch.wasManuallyClosed = false;
            this.deepResearch.isPanelOpen = shouldKeepOpen;
        },
        updateDeepResearch(payload: DeepResearchPayload) {
            if (payload.active === false) {
                this.resetDeepResearch(false);
                return;
            }

            const hasExistingData = this.deepResearch.isActive;

            if (payload.keywords) {
                this.deepResearch.keywords = payload.keywords;
            }
            if (payload.progressLog) {
                this.deepResearch.progressLog = payload.progressLog;
            }
            if (payload.steps) {
                this.deepResearch.steps = payload.steps;
            }
            if (payload.references) {
                this.deepResearch.references = payload.references;
            }
            if (typeof payload.summary === 'string') {
                this.deepResearch.summary = payload.summary;
            }
            if (payload.metadata) {
                this.deepResearch.metadata = payload.metadata;
            }
            if (payload.followUpQuestions) {
                this.deepResearch.followUpQuestions = payload.followUpQuestions;
            }

            this.deepResearch.isActive = (payload.active ?? hasExistingData) ||
                Boolean(
                    this.deepResearch.keywords.length ||
                    this.deepResearch.progressLog.length ||
                    this.deepResearch.steps.length ||
                    this.deepResearch.references.length ||
                    this.deepResearch.summary ||
                    Object.keys(this.deepResearch.metadata).length
                );

            if (this.deepResearch.isPanelOpen) {
                this.deepResearch.hasUnseenUpdates = false;
                this.deepResearch.wasManuallyClosed = false;
            } else if (this.deepResearch.wasManuallyClosed) {
                this.deepResearch.hasUnseenUpdates = true;
            } else {
                this.deepResearch.isPanelOpen = true;
                this.deepResearch.hasUnseenUpdates = false;
            }
        },
        toggleDeepResearch(force?: boolean) {
            if (typeof force === 'boolean') {
                this.deepResearch.isPanelOpen = force;
            } else {
                this.deepResearch.isPanelOpen = !this.deepResearch.isPanelOpen;
            }

            if (this.deepResearch.isPanelOpen) {
                this.deepResearch.hasUnseenUpdates = false;
                this.deepResearch.wasManuallyClosed = false;
            } else {
                this.deepResearch.wasManuallyClosed = true;
            }
        },
        triggerFollowUp(question: string) {
            const trimmed = question.trim();
            if (!trimmed) {
                return;
            }
            this.toggleDeepResearch(true);
            this.search(trimmed, null, { keepDeepResearchPanel: true });
        },
        /**
         * 初期シナリオ取得
         */
        async getFirstScenarios() {
            const config = getRuntimeConfig();

            try {
                const res = await $fetch<MessageDataScenarios[]>(`${config.public.scenarioApi}?query=&language=${this.language}`)
                return res;
            }
            catch (error) {
                console.error("初期シナリオ取得エラー", error);
                return []
            }
        },
        /**
         * 検索処理（質問回答API実行）
         * @param question
         */
        async search(question: string, fileData: FileData | null, options: SearchOptions = {}) {
            const config = getRuntimeConfig();
            await this.ensureUserId();
            const normalizedQuestion = question ?? '';
            const trimmedQuestion = normalizedQuestion.trim();
            const shouldSkipUserMessage = options?.skipUserMessage ?? false;
            const isFollowUp = options?.isFollowUp ?? false;
            const isRetry = options?.isRetry ?? false;
            const keepDeepResearchPanel = options?.keepDeepResearchPanel ?? false;
            const followUpTaskId = options?.followUpTaskId;

            const previousPayload = this.lastSearchPayload;
            const effectiveFileData = fileData ?? (isRetry && previousPayload?.fileData ? previousPayload.fileData : null);
            const hasImage = Boolean(effectiveFileData);

            if (!trimmedQuestion && !hasImage) {
                return null;
            }

            this.resetDeepResearch(keepDeepResearchPanel);

            if (!shouldSkipUserMessage) {
                const displayText = trimmedQuestion;
                const userMessage: Message = {
                    author: 'me',
                    type: 'text',
                    text: hasImage && !trimmedQuestion ? '' : displayText,
                    pages: [],
                    historyId: '',
                    answer: false,
                    scenarios: [],
                    isGoodReviewed: false,
                    isBadReviewed: false,
                    copyText: this.searchMarkdownMsg,
                    language: this.language,
                    reviewPattern: 0,
                    keywordStr: '',
                    keywordUrl: '',
                    questions: [],
                    imageUrl: hasImage ? effectiveFileData!.fileUrl : '',
                };
                this.messages.push(this.attachSessionContext(userMessage));
            }

            const typingMessage: Message = {
                author: 'support',
                type: 'typing',
                text: '',
                pages: [],
                historyId: '',
                answer: false,
                scenarios: [],
                isGoodReviewed: false,
                isBadReviewed: false,
                copyText: this.searchMarkdownMsg,
                language: this.language,
                reviewPattern: 0,
                keywordStr: this.keywordStr,
                keywordUrl: this.keywordUrl,
                questions: [],
                imageUrl: '',
                isFollowUp,
                followUpTaskId,
            };
            this.messages.push(this.attachSessionContext(typingMessage));

            this.searchResponseMsg = '';
            this.searchResponseClose = false;
            this.searchAnswerPages = [];
            this.isAnswerScenario = false;
            this.keywordStr = '';
            this.keywordUrl = '';
            this.questions = [];
            this.relatedQuestions = [];
            this.relatedQuestionsLang = "";

            const storedOptions: SearchOptions = { ...options, followUpTaskId };
            const payload: SearchPayload = {
                question: trimmedQuestion,
                fileData: effectiveFileData,
                options: storedOptions,
            };
            this.lastSearchPayload = payload;

            this.activeSearchResult = {
                question: trimmedQuestion,
                answerMarkdown: '',
                answerHtml: '',
                answerPlain: '',
                followUpTaskId: followUpTaskId ?? undefined,
            };
            const searchResult = this.activeSearchResult;

            this.streamBuffer = '';
            this.hasReceivedCloseEvent = false;
            this.streamTimedOut = false;
            this.isStreaming = true;
            this.canRetryLastSearch = false;

            const controller = new AbortController();
            this.activeStreamController = controller;

            try {

                const res = await fetch(config.public.searchApi as string, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'session-id': `${this.sessionId}`,
                        'user-id': this.userId || '',
                        Accept: 'text/event-stream',
                        cache: 'no-cache',
                    },
                    signal: controller.signal,
                    body: JSON.stringify(<SearchPostData>{
                        query: question,
                        lang: this.language,
                        mode: this.chatMode,
                        image_data: fileData ? fileData.base64 : "",
                        image_type: fileData ? fileData.fileType : "",
                        prompt_version: this.promptVersion // プロンプトバージョン指定
                    })
                });

                if (!res.ok || !res.body) {
                    throw new Error(`Failed to connect to search API: ${res.status}`);
                }

                const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();

                this.resetStreamTimeout();

                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    if (value) {
                        this.resetStreamTimeout();
                        this.processSearchResponseMsg(value);
                    }
                }

                this.processSearchResponseMsg('', { isFinal: true });
                this.handleStreamCompletion();
            } catch (error: any) {
                if (error?.name === 'AbortError') {
                    if (this.streamTimedOut) {
                        this.handleStreamError(new Error('SSE connection timed out'));
                    }
                } else {
                    this.handleStreamError(error);
                }
            } finally {
                this.clearStreamTimeout();
                this.activeStreamController = null;
                this.isStreaming = false;
            }

            return searchResult;
        },
        setActivePanel(panel: 'chat' | 'history') {
            this.activePanel = panel;
        },
        resetFollowUpData() {
            this.followUpQueue = [];
            this.followUpHistory = [];
            this.followUpSummary = '';
            this.isProcessingFollowUps = false;
            this.activePanel = 'chat';
            this.activeSearchResult = null;
        },

        /**
         * セッションID取得
         */
        resetStreamTimeout() {
            if (this.streamTimeoutHandle) {
                clearTimeout(this.streamTimeoutHandle);
            }
            this.streamTimedOut = false;
            this.streamTimeoutHandle = setTimeout(() => {
                this.streamTimedOut = true;
                this.activeStreamController?.abort();
            }, STREAM_TIMEOUT_MS);
        },
        clearStreamTimeout() {
            if (this.streamTimeoutHandle) {
                clearTimeout(this.streamTimeoutHandle);
                this.streamTimeoutHandle = null;
            }
        },
        handleStreamCompletion() {
            if (!this.hasReceivedCloseEvent) {
                this.handleStreamError(new Error('SSE stream closed without close event'));
                return;
            }
            this.canRetryLastSearch = false;
        },
        handleStreamError(error: unknown) {
            console.error('質問回答エラー', error);
            this.clearStreamTimeout();
            this.streamBuffer = '';
            this.hasReceivedCloseEvent = false;
            this.streamTimedOut = false;
            this.isStreaming = false;
            if (this.activeStreamController) {
                try {
                    this.activeStreamController.abort();
                } catch (e) {
                    console.debug('abort controller cleanup error', e);
                }
                this.activeStreamController = null;
            }

            const retryQuestions: FollowupQuestion[] = [];
            if (this.lastSearchPayload) {
                const hasQuestion = this.lastSearchPayload.question.trim().length > 0;
                const retryLabel = hasQuestion
                    ? `↻ ${this.lastSearchPayload.question}`
                    : (this.language?.startsWith('ja') ? '↻ 再試行' : '↻ Retry');
                retryQuestions.push({ label: retryLabel, value: this.lastSearchPayload.question, isRetry: true });
            }

            const errorMessage = {
                author: 'support',
                type: 'system_text',
                text: this.searchAnswerError,
                pages: [],
                historyId: '',
                answer: true,
                scenarios: [],
                isGoodReviewed: false,
                isBadReviewed: false,
                copyText: this.searchMarkdownMsg,
                language: this.language,
                reviewPattern: 0,
                keywordStr: this.keywordStr,
                keywordUrl: this.keywordUrl,
                questions: retryQuestions,
                imageUrl: '',
            } as Message;

            const lastMsg = this.messages[this.messages.length - 1];
            if (lastMsg && lastMsg.type == 'typing') {
                this.messages = [...this.messages.splice(0, this.messages.length - 1), errorMessage];
            } else {
                this.messages.push(errorMessage);
            }

            this.canRetryLastSearch = !!this.lastSearchPayload;
        },

        async getSessionId() {
            // セッションID取得API実行
            const config = useRuntimeConfig();
            try {
                const sessionId = await $fetch<string>(config.public.getSessionIdApi);
                this.sessionId = sessionId;
                const deepResearchStore = useDeepResearchStore();
                deepResearchStore.syncSession(this.sessionId);
            }
            catch (error) {
                //エラーの場合
                console.log("セッションID取得エラー", error);
                this.sessionId = "";
            }
        },
        /**
         * 質問回答APIレスポンス処理
         */
        processSearchResponseMsg(value: string, options?: { isFinal?: boolean }) {
            if (!value && !options?.isFinal) {
                return
            }
            this.streamBuffer += value ?? '';
            const arrData = this.streamBuffer.split(/\t\s*\t/)
            if (!options?.isFinal) {
                this.streamBuffer = arrData.pop() ?? '';
            } else {
                this.streamBuffer = '';
            }
            arrData.forEach((x) => {
                if (x.length > 0) {
                    // await this.sleep(5000);
                    // arr[0] = "event:scenario" arr[1] = "data:[]"
                    const arr = x.split("\t");
                    const [rawEvent, rawPayload] = arr;
                    if (!rawEvent || !rawPayload) {
                        return;
                    }
                    const eventType = rawEvent.split(":")[1] as SearchResponseEventType | undefined;
                    if (!eventType) {
                        return;
                    }
                    const data = rawPayload.substring(5);
                    this.handleSearchResponse(eventType, data);
                }
            })
        },
        /**
         * 質問回答APIレスポンスイベント処理
         * @param eventType 
         * @param data 
         */
        handleSearchResponse(eventType: SearchResponseEventType, data: string) {
            const config = getRuntimeConfig();
            const messageEvent = new MessageEvent(eventType, { data: data });
            // console.log("eventType", eventType);
            if (eventType == "text") {
                // テキスト
                // console.log("handleSearchResponse text", messageEvent.data);

                //自動スクロールオフ
                this.isAutoScrollToBottom = false;

                let resText = messageEvent.data;
                this.searchResponseMsg += resText;
                this.searchAnswerDebounce(this);
            } else if (eventType == "page") {
                // 関連ページ
                let pages = JSON.parse(messageEvent.data) as MessageDataPage[];

                this.searchAnswerPages = pages;
                this.searchResponseClose = true;
                this.searchAnswerDebounce(this);
            } else if (eventType == "keywords") {
                //　キーワード（サイト内検索）
                let keywords = JSON.parse(messageEvent.data) as string[];
                this.keywordStr = keywords.join(" ");
                this.keywordUrl = config.public.siteSearchUrl + this.keywordStr;
                this.searchAnswerDebounce(this);
            } else if (eventType == "related_questions") {
                try {
                    const payload = JSON.parse(messageEvent.data) as RelatedQuestionsPayload;
                    this.relatedQuestions = payload?.suggestions ?? [];
                    this.relatedQuestionsLang = payload?.lang || this.language;
                    this.searchAnswerDebounce(this);
                } catch (error) {
                    console.error("関連質問データの解析に失敗しました", error);
                }
            } else if (eventType == "question") {
                console.log("eventType question:", messageEvent.data);
                // 複数のQAがヒットしたとき
                let data = messageEvent.data.replace(/\n/g, "");
                this.questions.push(data);

                let newMsg = {
                    author: 'support',
                    text: this.scenarioMessage,
                    type: 'text',
                    pages: [],
                    id: Math.random(),
                    historyId: "",
                    answer: false,
                    scenarios: [],
                    isGoodReviewed: false,
                    isBadReviewed: false,
                    copyText: this.searchMarkdownMsg,
                    language: this.language,
                    reviewPattern: 0,
                    keywordStr: this.keywordStr,
                    keywordUrl: this.keywordUrl,
                    questions: this.questions,
                    imageUrl: ""
                } as Message;

                newMsg = this.attachSessionContext(newMsg);
                this.messages = [...this.messages.splice(0, this.messages.length - 1), newMsg]

            } else if (eventType == "intent_suggestions") {
                try {
                    const payload = JSON.parse(messageEvent.data) as { suggestions: IntentSuggestion[]; reason?: string };

                    const intentMsg = {
                        author: 'support',
                        text: '',
                        type: 'intent_suggestions',
                        pages: [],
                        id: Math.random(),
                        historyId: '',
                        answer: false,
                        scenarios: [],
                        isGoodReviewed: false,
                        isBadReviewed: false,
                        copyText: '',
                        language: this.language,
                        reviewPattern: 0,
                        keywordStr: this.keywordStr,
                        keywordUrl: this.keywordUrl,
                        questions: [],
                        imageUrl: '',
                        intentSuggestions: {
                            suggestions: payload.suggestions ?? [],
                            reason: payload.reason ?? ''
                        },
                    } as Message;

                    const lastMsg = this.messages[this.messages.length - 1];
                    if (lastMsg && lastMsg.type == 'typing') {
                        this.messages = [...this.messages.splice(0, this.messages.length - 1), this.attachSessionContext(intentMsg)];
                    } else {
                        this.messages.push(this.attachSessionContext(intentMsg));
                    }
                } catch (error) {
                    console.error('インテントサジェストの解析に失敗しました', error);
                }

            } else if (eventType == "scenario") {
                // シナリオ
                this.isAnswerScenario = true;
                let scenarios = JSON.parse(messageEvent.data) as MessageDataScenarios[];

                let newMsg = {
                    author: 'support',
                    text: this.scenarioMessage,
                    type: 'text',
                    pages: [],
                    id: Math.random(),
                    historyId: "",
                    answer: false,
                    scenarios: scenarios,
                    isGoodReviewed: false,
                    isBadReviewed: false,
                    copyText: this.searchMarkdownMsg,
                    language: this.language,
                    reviewPattern: 0,
                    keywordStr: this.keywordStr,
                    keywordUrl: this.keywordUrl,
                    questions: [],
                    imageUrl: ""
                } as Message;

                newMsg = this.attachSessionContext(newMsg);
                this.messages = [...this.messages.splice(0, this.messages.length - 1), newMsg]
            } else if (eventType == "close") {
                // クローズ
                this.hasReceivedCloseEvent = true;
                setTimeout(() => {
                    //自動スクロールオン
                    this.isAutoScrollToBottom = true;

                    const config = getRuntimeConfig();
                    document.querySelectorAll(".chat-container sup").forEach((x) => {
                        // console.log("addEventToSup, x:", x)
                        if (x.classList.contains("v-popper--has-tooltip")) {
                            return
                        }

                        const targetElem = x as HTMLElement;
                        const tooltip = createTooltip(targetElem, {
                            triggers: ['hover', 'click'],
                            popperTriggers: ['hover'],
                            content: this.createSearchAnswerTooltipHtml(targetElem.innerText, config.public.googleTranslateUrl),
                            html: true,
                            theme: 'dropdown'
                        }, {});
                        this.searchAnswerTooltips.push(tooltip);
                    })
                }, 1000);
            } else if (eventType == "history_id") {
                // 履歴
                if (this.activeSearchResult) {
                    this.activeSearchResult.historyId = messageEvent.data;
                }
                setTimeout(() => {
                    let lastMsg = this.messages[this.messages.length - 1] as Message;
                    // let msg = this.messages[this.messages.length - 2] as Message;
                    lastMsg.historyId = messageEvent.data;
                    lastMsg.sessionId = this.sessionId || undefined;
                    // console.log("handleHistoryId", lastMsg.historyId);
                    this.messages = [...this.messages.splice(0, this.messages.length - 1), lastMsg];

                    const deepResearchStore = useDeepResearchStore();
                    deepResearchStore.syncHistoryId(messageEvent.data);

                    const lastContentMsg = this.messages[this.messages.length - 1];
                    const isIntentSuggestions = lastContentMsg?.type === 'intent_suggestions';

                    if (config.public.reviewPattern == 1 && !this.isAnswerScenario && this.questions.length == 0 && !isIntentSuggestions) {

                        let reviewMsg = {
                            author: 'support',
                            text: '',
                            type: 'text',
                            pages: [],
                            id: Math.random(),
                            historyId: messageEvent.data,
                            answer: true,
                            scenarios: [],
                            isGoodReviewed: false,
                            isBadReviewed: false,
                            copyText: "",
                            language: this.language,
                            reviewPattern: config.public.reviewPattern,
                            keywordStr: "",
                            keywordUrl: "",
                            questions: [],
                            imageUrl: ""
                        } as Message
                        reviewMsg = this.attachSessionContext(reviewMsg);
                        this.messages.push(reviewMsg);
                    }
                }, 600);
            } else if (eventType == "deep_research_reset") {
                this.resetDeepResearch(false);
            } else if (eventType == "deep_research") {
                try {
                    const payload = JSON.parse(messageEvent.data) as DeepResearchPayload;
                    this.updateDeepResearch(payload);
                } catch (error) {
                    console.error("ディープリサーチデータの解析に失敗しました", error);
                }
            } else if (eventType == "deep_research_followup") {
                try {
                    const followups = JSON.parse(messageEvent.data) as string[];
                    this.updateDeepResearch({ followUpQuestions: followups, active: true });
                } catch (error) {
                    console.error("フォローアップ質問の解析に失敗しました", error);
                }
            } else if (eventType == "system_text") {
                // console.log("handleSearchResponse progress", messageEvent.data);
                let resText = messageEvent.data;

                resText = resText.replace(/\n/g, "<br>").replace(/\\n/g, "<br>");

                let newMsg = {
                    author: 'support',
                    text: resText,
                    type: 'system_text',
                    pages: [],
                    id: Math.random(),
                    historyId: "",
                    answer: false,
                    scenarios: [],
                    isGoodReviewed: false,
                    isBadReviewed: false,
                    copyText: "",
                    language: this.language,
                    reviewPattern: 0,
                    keywordStr: "",
                    keywordUrl: "",
                    questions: [],
                    imageUrl: ""
                } as Message;

                newMsg = this.attachSessionContext(newMsg);
                // 最後のメッセージのtypeが'typing'の場合は更新
                const lastMsg = this.messages[this.messages.length - 1];
                if (lastMsg && (lastMsg.type == 'typing' || this.chatMode == ChatMode.general)) {
                    // タイピングメッセージを更新
                    this.messages = [...this.messages.splice(0, this.messages.length - 1), newMsg]
                } else {
                    // 新しいメッセージを追加
                    this.messages.push(newMsg);
                }
            }
        },
        /**
         * 質問回答ツールチップHTML生成
         */
        createSearchAnswerTooltipHtml(no: string, googleTranslateUrl: string) {
            const page = this.searchAnswerPages.find!(x => x.no == no)
            const translateUrl = googleTranslateUrl + "?sl=ja&tl=" + this.language + "&u=" + page?.url;
            if (this.language == "ja") {
                //言語が日本語の場合
                return `<div class="reference-link">
              <p class="title"> <a href="${page?.url}" target = "_blank" class="${this.createLanguageClass('ja')}"> ${page?.title} </a></p>
            </div>`
            }
            else {
                //言語が日本語以外の場合
                return `<div class= "reference-link ${this.createLanguageClass(this.language)}">
                <p class="title"><a href="${page?.url}" target="_blank" class="${this.createLanguageClass('ja')}">${page?.title}</a></p>
                    <p class="title"><a href="${translateUrl}" target="_blank" class="${this.createLanguageClass(this.language)}">${page?.translate_title}</a></p>
                        </div>
                            `;
            }
        },
        /**
         * 質問回答デバウンス（反映）処理
         */
        searchAnswerDebounce: useDebounceFn((context) => {
            // 回答テキスト内の文字調整
            const config = getRuntimeConfig();
            const rawResponse = context.searchResponseMsg ?? "";

            const markdownSource = rawResponse
                .replace(/\r\n/g, "\n")
                .replace(/\\n/g, "\n");

            context.searchMarkdownMsg = rawResponse;

            const contextPlaceholderMap = new Map<string, string>();
            let placeholderIndex = 0;
            const preparedMarkdown = markdownSource.replace(/\[Context\.(\d+)\]/g, (_match: string, ref: string) => {
                const token = `@@CONTEXT_${placeholderIndex++}@@`;
                contextPlaceholderMap.set(token, ref);
                return token;
            });
            // console.log("preparedMarkdown:", preparedMarkdown);
            let msg = micromark(preparedMarkdown, { allowDangerousHtml: true });
            // console.log("msg:", msg);
            contextPlaceholderMap.forEach((ref, token) => {
                msg = msg.split(token).join(`<sup>${ref}</sup>`);
            });

            // console.log(`searchAnswerDebounce msg ${msg} `);

            //回答文内のURLをリンクに変換
            msg = msg.replace(/&amp;/g, "&");
            msg = msg.replace(/&quot;/g, "\"");
            const urlRegex = /https?:\/\/[a-zA-Z0-9._%\u4e00-\u9faf\u3040-\u309F\u30A0-\u30FF\s/?&#=-]+/g;
            let urls = msg.match(urlRegex);
            if (urls) {
                urls.forEach((x: string) => {
                    msg = msg.replace(x, `<a href="${x}" target="_blank">${x}</a>`);
                });
            }

            if (context.activeSearchResult) {
                context.activeSearchResult.answerMarkdown = context.searchResponseMsg;
                context.activeSearchResult.answerHtml = msg;
                context.activeSearchResult.answerPlain = context.stripHtmlTags(msg);
            }

            let newMsg = {
                author: 'support',
                text: msg,
                type: 'text',
                pages: [],
                id: Math.random(),
                historyId: "",
                answer: true,
                scenarios: [],
                isGoodReviewed: false,
                isBadReviewed: false,
                copyText: context.searchMarkdownMsg,
                language: context.language,
                reviewPattern: 0,
                keywordStr: context.keywordStr,
                keywordUrl: context.keywordUrl,
                questions: context.questions,
                relatedQuestions: context.relatedQuestions,
                relatedQuestionsLang: context.relatedQuestionsLang || context.language,
                imageUrl: "",
                isFollowUp: context.activeSearchResult?.followUpTaskId ? true : undefined,
                followUpTaskId: context.activeSearchResult?.followUpTaskId
            } as Message

            const contextualizedMsg = context.attachSessionContext(newMsg);
            const lastMsg = context.messages[context.messages.length - 1];
            if (lastMsg && lastMsg.type == "system_text" && context.chatMode == ChatMode.gomi) {
                context.messages.push(newMsg);
            } else {
                if (context.searchResponseClose) {
                    newMsg.pages = context.searchAnswerPages;
                    context.messages = [...context.messages.splice(0, context.messages.length - 1), contextualizedMsg]
                    console.log("hanei- sendClosed", performance.now())
                } else {
                    context.messages = [...context.messages.splice(0, context.messages.length - 1), contextualizedMsg]
                }
            }
        }, 10, { maxWait: 500 }),
        stripHtmlTags(value: string) {
            if (!value) {
                return '';
            }

            return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        },

        /**
         * 評価登録（高評価）
         */
        async sendGoodReview(historyId: string, isCancel: boolean) {
            const config = getRuntimeConfig();
            let result = false;

            try {

                let body = isCancel ?
                    { "history_id": historyId } :
                    { "history_id": historyId, "evaluate": Evaluate.good };

                const res = await $fetch<{ result: boolean }>(config.public.reviewApi, {
                    method: 'POST',
                    body: body
                });

                result = res.result;
                const messages = this.messages.filter(x => x.historyId == historyId);
                if (messages && result) {
                    messages.forEach(message => {
                        if (isCancel) {
                            message.isGoodReviewed = false;
                        }
                        else {
                            message.isGoodReviewed = true;
                            message.isBadReviewed = false;
                        }
                    });
                }
            } catch (error) {
                console.error("高評価登録エラー", error);
            }

            return result;
        },
        /**
         * 評価登録（低評価）
         */
        async sendBadReview(reviewData: ReviewData, isCancel: boolean) {
            const config = getRuntimeConfig();
            let result = false;

            try {
                let body = isCancel ?
                    { "history_id": reviewData.historyId } :
                    { "history_id": reviewData.historyId, "evaluate": Evaluate.bad, "comment": reviewData.comment };

                const res = await $fetch<{ result: boolean }>(config.public.reviewApi, {
                    method: 'POST',
                    body: body
                });

                result = res.result;
                const messages = this.messages.filter(x => x.historyId == reviewData.historyId);
                if (messages && result) {
                    messages.forEach(message => {

                        if (isCancel) {
                            message.isBadReviewed = false;
                        }
                        else {
                            message.isBadReviewed = true;
                            message.isGoodReviewed = false;
                        }
                    });

                }
            } catch (error) {
                console.error("低評価登録エラー", error);
            }

            return result;
        },
        /**
         * 引数に渡されたパスにアプリケーションUrlを追加して返す
         * HTMLに直接指定せず動的にURLを指定場合に使用する。仮想ディレクトリにアプリケーションをデプロイした時の対応
         * @param path
         *  静的ファイル読み込みか
         *  パス（先頭のスラッシュはなし）
         * @returns
         *  生成されたURL
         */
        createUrl(isLoadStatic: boolean, path: string) {
            const config = getRuntimeConfig();
            return isLoadStatic ? `${config.public.cdnUrl}${config.public.staticSitePath}/${path}` : `${config.public.cdnUrl}${path}`;
        },
        /**
         * 言語毎のフォント切り替え用のクラスを作成
         * @param language 
         */
        createLanguageClass(language: string) {
            return language == "" ? "" : `font-lang-${language}`;
        }
    },
    persist: {
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
        paths: ['userId'],
        omit: ['isDspStartChat', 'windowClosed', 'windowExtend', 'language', 'languageMenus', 'currentMode']
    }
});

export function useChatStoreWithWatch() {
    const store = useChatStore();

    // watchでmessagesの変更を監視してスクロールする
    watch(
        () => store.messages,
        (newMessages) => {
            setTimeout(() => {
                if (store.isAutoScrollToBottom) {
                    scrollToBottom();
                }
            }, 100);
        },
        { deep: true }
    );


    /**
     * チャット画面を自動スクロールする
     */
    const scrollToBottom = () => {
        // console.log("call scrollToBottom");
        if (typeof window !== "undefined") {
            const container = document.querySelector(".messageContainer");
            if (container) {
                // console.log("scrollToBottom", container.scrollHeight);
                container.scrollTop = container.scrollHeight;
            }
        }
    };

    // watchでチャットモードの変更を監視
    watch(
        () => store.chatMode,
        (newChatMode, oldChatMode) => {
            if (store.suppressModeChangeNotice) {
                return;
            }
            console.log(`Chat mode changed to: ${newChatMode}, from: ${oldChatMode}`);

            if (newChatMode === "general") {
                // 総合案内モード

                // メッセージ表示
                let message = {
                    author: "support",
                    type: "text",
                    text: store.changeChatModeGeneralMessage,
                    pages: [],
                    historyId: "",
                    answer: false,
                    scenarios: [],
                    isGoodReviewed: false,
                    isBadReviewed: false,
                    copyText: "",
                    language: store.language,
                    reviewPattern: 0,
                    keywordStr: "",
                    keywordUrl: "",
                    questions: [],
                    imageUrl: ""
                } as Message;
                message = store.attachSessionContext(message);
                store.messages.push(message);
            }
            else if (newChatMode === "gomi") {
                // ごみ分別案内モード

                // メッセージ表示
                let message = {
                    author: "support",
                    type: "text",
                    text: `${store.changeChatModeGomiMessage}</br>${store.chatModeGomiStartMessage}`,
                    pages: [],
                    historyId: "",
                    answer: false,
                    scenarios: [],
                    isGoodReviewed: false,
                    isBadReviewed: false,
                    copyText: "",
                    language: store.language,
                    reviewPattern: 0,
                    keywordStr: "",
                    keywordUrl: "",
                    questions: [],
                    imageUrl: ""
                } as Message;
                message = store.attachSessionContext(message);
                store.messages.push(message);

            } else {
                // その他のモード（DBなど）

                // 現在のモード情報を取得
                const currentModeInfo = store.modes.find((m) => m.code === newChatMode);

                const modeName = currentModeInfo ? store.getModeDisplayName(currentModeInfo) : "";
                const description = currentModeInfo ? store.getModeDisplayDescription(currentModeInfo) : "";
                const parts = [store.uiTextModeSwitched, description].filter((x) => !!x);

                // メッセージ表示
                let message = {
                    author: "support",
                    type: "text",
                    text: parts.join("</br>"),
                    pages: [],
                    historyId: "",
                    answer: false,
                    scenarios: [],
                    isGoodReviewed: false,
                    isBadReviewed: false,
                    copyText: "",
                    language: store.language,
                    reviewPattern: 0,
                    keywordStr: "",
                    keywordUrl: "",
                    questions: [],
                    imageUrl: ""
                } as Message;
                message = store.attachSessionContext(message);
                store.messages.push(message);
            }
        },
        { deep: true }
    );

    return store;
}

export function useChatStoreLanguage() {
    const store = useChatStore();
    const { locale, t, setLocale } = useI18n({ useScope: 'global' });

    /**
     * 言語初期セット
     */
    const setInit = () => {

        console.log("setInit language:", store.language);

        store.uiTextAttention = t("attention");
        store.uiTextQuestionInputPlaceholder = t('questionInputPlaceholder');
        store.uiTextModeSwitched = t('modeSwitched');
        store.startMessage = t('startMessage');
        store.startScenarioMessage = t('startScenarioMessage');
        store.searchAnswerError = t('searchAnswerError');
        store.scenarioMessage = t('scenarioMessage');
        store.changeChatModeGeneralMessage = t('changeModeGeneral');
        store.changeChatModeGomiMessage = t('changeModeGomi');
        store.chatModeGomiStartMessage = t('startMessageChatModeGomi');
        createLanguageMenus();

        //i18nのロケールを監視（ロケールを変更されたらコールされる）
        watch(locale, async (newLocale) => {
            console.log("locale changed:", newLocale);
            store.uiTextAttention = t("attention");
            store.uiTextQuestionInputPlaceholder = t('questionInputPlaceholder');
            store.uiTextModeSwitched = t('modeSwitched');
            store.startMessage = t('startMessage');
            store.startScenarioMessage = t('startScenarioMessage');
            store.searchAnswerError = t('searchAnswerError');
            store.scenarioMessage = t('scenarioMessage');
            store.changeChatModeGeneralMessage = t('changeModeGeneral');
            store.changeChatModeGomiMessage = t('changeModeGomi');
            store.chatModeGomiStartMessage = t('startMessageChatModeGomi');

            setTimeout(() => {
                createLanguageMenus();
            }, 100);

            // 言語切替したタイミングで、特別モード表示用の翻訳も必要な分だけ取得する
            await store.fetchModes();
            await store.initDsp();
        });
    }

    /**
     * 言語メニュークリック
     * @param language
     * 言語
     * @returns
     * なし
     */
    const clickLanguageMenu = (language: string) => {
        store.language = language;
        store.isLanguageMenuDsp = false;
        setLocale(language);
        locale.value = language.replace("-", "");
        console.log("clickLanguageMenu", language, locale.value);
    }

    /**
     * 言語メニュー生成
     * @returns
     * 言語メニュー
     */
    const createLanguageMenus = () => {
        const config = getRuntimeConfig();
        const supported = config.public.supportedLanguages as string[];
        store.languageMenus = supported.filter((x: string) => x !== store.language);
    }

    return { setInit, clickLanguageMenu };
}
